import React, { useState, useEffect } from "react";
import { X, ArrowRight, Loader2, CheckCircle2, Eye, EyeOff, Lock, Mail, KeyRound, ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ACCENT, GOLD, DARK, CARD, MID, HUB_META, MARKET_TOKEN_KEY, MARKET_TERMS_KEY } from "./constants";

// ── Helpers ───────────────────────────────────────────────────────────────────
function getFingerprint() {
  const existing = localStorage.getItem("hc_fp");
  if (existing) return existing;
  const raw = [
    navigator.userAgent || "",
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    `${new Date().getTimezoneOffset()}`,
    navigator.language || "",
    `${navigator.hardwareConcurrency || 0}`,
    navigator.platform || "",
  ].join("|");
  let h = 0;
  for (let i = 0; i < raw.length; i++) h = ((h << 5) - h + raw.charCodeAt(i)) | 0;
  const fp = Math.abs(h).toString(36) + raw.length.toString(36);
  localStorage.setItem("hc_fp", fp);
  return fp;
}

function getDeviceInfo() {
  return {
    ua:         (navigator.userAgent || "").slice(0, 300),
    screen_res: `${screen.width}x${screen.height}`,
    tz_offset:  `${new Date().getTimezoneOffset()}`,
    language:   navigator.language || "",
    platform:   navigator.platform || "",
    hw_cores:   `${navigator.hardwareConcurrency || 0}`,
  };
}

// ── PwField ───────────────────────────────────────────────────────────────────
function PwField({ label, value, onChange, placeholder = "Min. 8 characters", "data-testid": testId }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      {label && (
        <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">{label}</label>
      )}
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          data-testid={testId}
          className="w-full bg-[#111] border border-white/10 text-white px-3 py-2.5 pr-10 text-sm focus:outline-none focus:border-[#FF4D00] transition-colors"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70"
        >
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

// ── BannedScreen ──────────────────────────────────────────────────────────────
function BannedScreen() {
  return (
    <div className="fixed inset-0 z-[999] flex flex-col items-center justify-center" style={{ background: "#0a0a0a" }}>
      <div className="text-center max-w-md px-6">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)" }}>
          <X className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="font-head text-2xl font-bold uppercase tracking-[0.15em] text-red-500 mb-3">Access Denied</h1>
        <p className="text-white/50 text-sm leading-relaxed mb-2">
          You have been permanently banned from the Hylas Companion Market.
        </p>
        <p className="text-white/30 text-xs">
          This action cannot be reversed. Contact support if you believe this is an error.
        </p>
      </div>
    </div>
  );
}

// ── US States ────────────────────────────────────────────────────────────────
const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

// ── SetupWizard ───────────────────────────────────────────────────────────────
export function SetupWizard({ onComplete }) {
  const [form, setForm]       = useState({ admin: "", carrier: "", recruiter: "", safety: "", driver: "" });
  const [confirm, setConfirm] = useState({ admin: "", carrier: "", recruiter: "", safety: "", driver: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const rows = [
    ["admin", "Admin Master Password"],
    ["carrier", "Carrier Hub"],
    ["recruiter", "Recruiter Hub"],
    ["safety", "Safety Hub"],
    ["driver", "Driver Hub"],
  ];

  async function submit(e) {
    e.preventDefault();
    setError("");
    for (const [k, label] of rows) {
      if (form[k].length < 8) { setError(`${label}: min 8 characters`); return; }
      if (form[k] !== confirm[k]) { setError(`${label}: passwords don't match`); return; }
    }
    setLoading(true);
    try {
      await api.marketSetup({
        admin_password: form.admin, carrier_password: form.carrier,
        recruiter_password: form.recruiter, safety_password: form.safety, driver_password: form.driver,
      });
      toast.success("Market configured!");
      onComplete();
    } catch (err) {
      setError(err?.response?.data?.detail || "Setup failed.");
    } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.95)" }}>
      <div className="w-full max-w-md overflow-y-auto" style={{ background: CARD, border: `1px solid rgba(245,200,66,0.3)`, maxHeight: "90vh" }}>
        <div className="px-7 pt-7 pb-5" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <p className="font-head text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: GOLD }}>First-Time Setup</p>
          <h2 className="font-head text-xl font-bold uppercase tracking-[0.1em] text-white">
            Welcome to <span style={{ color: GOLD }}>Hylas Market</span>
          </h2>
          <p className="text-white/40 text-xs mt-1.5">Set passwords for all 5 hubs. The Admin password unlocks all of them.</p>
        </div>
        <form onSubmit={submit} className="px-7 py-5 space-y-4">
          {rows.map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <PwField label={label} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
              <PwField value={confirm[k]} onChange={(e) => setConfirm((f) => ({ ...f, [k]: e.target.value }))} placeholder="Confirm password" />
            </div>
          ))}
          {error && <p className="text-red-400 text-sm bg-red-400/10 px-3 py-2">{error}</p>}
          <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 font-head text-sm uppercase tracking-[0.2em] py-3.5 disabled:opacity-60" style={{ background: GOLD, color: DARK }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Configure Market
          </button>
        </form>
      </div>
    </div>
  );
}

// ── DriverSelfApplyModal ──────────────────────────────────────────────────────
export function DriverSelfApplyModal({ onClose, referralCode }) {
  const [form, setForm]     = useState({ first_name: "", last_name: "", phone: "", state: "", cdl_class: "", position: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");
  const [success, setSuccess] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full bg-[#111] border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#4ADE80] transition-colors";
  const lbl = (txt) => <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">{txt}</label>;

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.first_name || !form.last_name || !form.phone || !form.state || !form.cdl_class) {
      setError("Please fill in all required fields."); return;
    }
    setLoading(true);
    try {
      await api.marketDriverSelfApply({ ...form, referred_by: referralCode || null });
      setSuccess(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.96)" }}>
      <div className="w-full max-w-sm overflow-y-auto" style={{ background: CARD, border: "1px solid rgba(74,222,128,0.3)", maxHeight: "90vh" }}>
        <div className="px-6 pt-6 pb-4 flex items-start justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div>
            <span className="font-head text-[10px] uppercase tracking-[0.25em] mb-1 block" style={{ color: "#4ADE80" }}>Driver Hub</span>
            <h2 className="font-head text-lg font-bold uppercase tracking-[0.1em] text-white">Apply to the Market</h2>
            <p className="text-white/30 text-[10px] mt-0.5">Admin reviews within 24–48 hours.</p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white ml-4 transition-colors"><X className="w-5 h-5" /></button>
        </div>
        {success ? (
          <div className="px-6 py-8 text-center">
            <p className="font-head text-base font-bold text-white mb-2">Application Submitted!</p>
            <p className="text-white/50 text-sm">An admin will review your profile within 24–48 hours. Once approved, you can log in with your name and phone.</p>
            <button onClick={onClose} className="mt-6 px-6 py-2.5 font-head text-xs uppercase tracking-[0.2em] transition-colors hover:brightness-110" style={{ background: "#4ADE80", color: DARK }}>Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>{lbl("First Name *")}<input type="text" data-testid="self-apply-first-name" value={form.first_name} onChange={set("first_name")} className={inputCls} placeholder="First" /></div>
              <div>{lbl("Last Name *")}<input type="text" data-testid="self-apply-last-name" value={form.last_name} onChange={set("last_name")} className={inputCls} placeholder="Last" /></div>
            </div>
            <div>{lbl("Phone Number *")}<input type="tel" data-testid="self-apply-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(000) 000-0000" /></div>
            <div>{lbl("State *")}
              <select data-testid="self-apply-state" value={form.state} onChange={set("state")} className={inputCls + " appearance-none"} style={{ cursor: "pointer" }}>
                <option value="">Select state</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>{lbl("CDL Class *")}
              <select data-testid="self-apply-cdl" value={form.cdl_class} onChange={set("cdl_class")} className={inputCls + " appearance-none"} style={{ cursor: "pointer" }}>
                <option value="">Select CDL class</option>
                <option value="CDL-A">CDL-A</option>
                <option value="CDL-B">CDL-B</option>
                <option value="CDL-C">CDL-C</option>
              </select>
            </div>
            <div>{lbl("Position / Equipment")}
              <select data-testid="self-apply-position" value={form.position} onChange={set("position")} className={inputCls + " appearance-none"} style={{ cursor: "pointer" }}>
                <option value="">Select position</option>
                <option value="OTR — Dry Van">OTR — Dry Van</option>
                <option value="OTR — Flatbed">OTR — Flatbed</option>
                <option value="OTR — Refrigerated">OTR — Refrigerated</option>
                <option value="Regional — Dry Van">Regional — Dry Van</option>
                <option value="Local — Box Truck">Local — Box Truck</option>
                <option value="Lease Purchase — Semi">Lease Purchase — Semi</option>
              </select>
            </div>
            <div>{lbl("Email (optional)")}<input type="email" data-testid="self-apply-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="your@email.com" /></div>
            {error && <p className="text-red-400 text-sm bg-red-400/10 px-3 py-2">{error}</p>}
            <button type="submit" data-testid="self-apply-submit" disabled={loading} className="w-full flex items-center justify-center gap-2 font-head text-sm uppercase tracking-[0.2em] py-3.5 disabled:opacity-60 transition-all hover:brightness-110" style={{ background: "#4ADE80", color: DARK }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Submit Application
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── LoginModal ────────────────────────────────────────────────────────────────
export function LoginModal({ hub, onSuccess, onClose }) {
  const meta    = HUB_META[hub] || {};
  const HubIcon = meta.icon || Lock;
  const isAdmin     = hub === "admin";
  const isCarrier   = hub === "carrier";
  const isDriver    = hub === "driver";
  const isRecruiter = hub === "recruiter";
  const isSafety    = hub === "safety";

  const [form, setForm] = useState({
    username: "", password: "", carrier_name: "", carrier_phone: "",
    first_name: "", last_name: "", phone: "",
    name: "", email: "",
    company_name: "", direct_phone: "",
  });
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState("");
  const [banned, setBanned]                 = useState(false);
  const [rememberMe, setRememberMe]         = useState(true);
  const [cooldownEnd, setCooldownEnd]       = useState(null);
  const [cooldownDisplay, setCooldownDisplay] = useState("");
  const [showSelfApply, setShowSelfApply]   = useState(() => Boolean(new URLSearchParams(window.location.search).get("ref")));

  // Email+Password alternative login
  // Only Carrier defaults to emailMode; Safety uses hub credentials (Safety/Safety2027#$)
  const [emailMode, setEmailMode]     = useState(() => hub === "carrier");
  const [emailForm, setEmailForm]     = useState({ email: "", password: "" });
  const [forgotPw, setForgotPw]       = useState(false);
  const [forgotSent, setForgotSent]   = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // Carrier self-registration
  const [showRegister, setShowRegister]   = useState(false);
  const [regForm, setRegForm]             = useState({ company_name: "", email: "", password: "", confirm: "" });
  const setReg = (k) => (e) => setRegForm((f) => ({ ...f, [k]: e.target.value }));

  const _refCode = new URLSearchParams(window.location.search).get("ref") || "";
  useEffect(() => {
    if (_refCode) setShowSelfApply(true);
  }, [_refCode]);

  useEffect(() => {
    if (!cooldownEnd) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((cooldownEnd - Date.now()) / 1000));
      if (remaining <= 0) { setCooldownEnd(null); setCooldownDisplay(""); return; }
      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;
      setCooldownDisplay(h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cooldownEnd]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setError("");
    const fp = getFingerprint();

    if (isAdmin && (!form.username || !form.password)) { setError("Username and password required."); return; }
    if (isCarrier && (!form.username || !form.password || !form.carrier_name || !form.email)) { setError("All fields including company email are required."); return; }
    if (isDriver && (!form.first_name || !form.phone)) { setError("First name and phone are required."); return; }
    if (isRecruiter && (!form.name || !form.email)) { setError("Name and email are required."); return; }
    if (isRecruiter && (!form.username || !form.password)) { setError("Hub username and password are required."); return; }
    if (isSafety && (!form.company_name || !form.email)) { setError("Company name and email are required."); return; }
    if (isSafety && (!form.username || !form.password)) { setError("Hub username and password are required."); return; }

    setLoading(true);
    try {
      if (isRecruiter || isSafety) {
        const di = getDeviceInfo();
        const bc = await api.marketCheckBan({
          fingerprint: fp, email: form.email.trim().toLowerCase(),
          ua: di.ua, screen_res: di.screen_res, tz_offset: di.tz_offset,
        }).catch(() => ({ banned: false }));
        if (bc.banned) { setBanned(true); setLoading(false); return; }
      }

      const payload = { hub, fingerprint: fp, device_info: getDeviceInfo() };
      if (isAdmin || isCarrier)  Object.assign(payload, { username: form.username.trim(), password: form.password, carrier_name: form.carrier_name.trim(), email: form.email.trim(), carrier_phone: form.carrier_phone.trim() });
      if (isDriver)    Object.assign(payload, { first_name: form.first_name.trim(), last_name: form.last_name.trim(), phone: form.phone.trim() });
      if (isRecruiter) Object.assign(payload, { name: form.name.trim(), email: form.email.trim(), username: form.username.trim(), password: form.password });
      if (isSafety)    Object.assign(payload, { company_name: form.company_name.trim(), email: form.email.trim(), direct_phone: form.direct_phone.trim(), username: form.username.trim(), password: form.password });

      const res = await api.marketLogin(payload);
      // Always persist to localStorage — global persistent login (no session-only)
      localStorage.setItem(MARKET_TOKEN_KEY, res.token);
      sessionStorage.removeItem(MARKET_TOKEN_KEY); // clear any stale session copy
      onSuccess(res);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429 && typeof detail === "object" && detail?.cooldown_seconds) {
        setCooldownEnd(Date.now() + detail.cooldown_seconds * 1000);
        setError(detail.message || "Too many attempts. Please wait.");
        return;
      }
      if (detail === "PERMANENTLY_BANNED") { setBanned(true); return; }
      setError(typeof detail === "string" ? detail : "Incorrect credentials. Please try again.");
    } finally { setLoading(false); }
  }

  async function submitEmail(e) {
    e.preventDefault();
    setError("");
    if (!emailForm.email || !emailForm.password) { setError("Email and password are required."); return; }
    setLoading(true);
    try {
      const res = await api.marketAccountLogin(emailForm.email.trim(), emailForm.password);
      // Always persist to localStorage — global persistent login
      localStorage.setItem(MARKET_TOKEN_KEY, res.token);
      sessionStorage.removeItem(MARKET_TOKEN_KEY);
      onSuccess(res);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429 && typeof detail === "object" && detail?.cooldown_seconds) {
        setCooldownEnd(Date.now() + detail.cooldown_seconds * 1000);
        setError(detail.message || "Too many attempts. Please wait.");
        return;
      }
      setError(typeof detail === "string" ? detail : "Incorrect email or password.");
    } finally { setLoading(false); }
  }

  async function submitForgot(e) {
    e.preventDefault();
    setError("");
    if (!emailForm.email) { setError("Enter your email address."); return; }
    setLoading(true);
    try {
      await api.marketAccountForgotPassword(emailForm.email.trim());
      setForgotSent(true);
    } catch { setError("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  }

  async function submitRegister(e) {
    e.preventDefault();
    setError("");
    if (!regForm.company_name.trim()) { setError("Company name is required."); return; }
    if (!regForm.email.trim() || !regForm.email.includes("@")) { setError("A valid email is required."); return; }
    if (regForm.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (regForm.password !== regForm.confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      const res = await api.marketCarrierRegister({
        company_name: regForm.company_name.trim(),
        email:        regForm.email.trim().toLowerCase(),
        password:     regForm.password,
      });
      localStorage.setItem(MARKET_TOKEN_KEY, res.token);
      onSuccess(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Registration failed. Please try again.");
    } finally { setLoading(false); }
  }

  function handleGoogleSignIn() {
    // DO NOT add fallback or custom redirect logic — see Emergent Auth docs
    const redirectUrl = window.location.origin + "/market";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  if (banned) return <BannedScreen />;

  const inputCls = "w-full bg-[#111] border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00] transition-colors";
  const label = (txt) => (
    <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">{txt}</label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.92)" }}>
      <div className="w-full max-w-sm" style={{ background: CARD, border: `1px solid rgba(${meta.accent === ACCENT ? "255,77,0" : "245,200,66"},0.3)` }}>
        <div className="px-6 pt-6 pb-4 flex items-start justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <HubIcon className="w-4 h-4" style={{ color: meta.accent }} />
              <span className="font-head text-[10px] uppercase tracking-[0.25em]" style={{ color: meta.accent }}>{meta.label} Hub</span>
            </div>
            <h2 className="font-head text-lg font-bold uppercase tracking-[0.1em] text-white">Enter Hub</h2>
            <p className="text-white/30 text-[10px] mt-0.5">
              {isDriver    ? "Enter your name and phone number to find your profile." :
               isRecruiter ? "Identify yourself to access the Recruiter Hub." :
               isSafety    ? "Hub credentials required." :
                             "Hub credentials required."}
            </p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white ml-4 transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={emailMode ? (forgotPw ? submitForgot : (showRegister ? submitRegister : submitEmail)) : submit} className="px-6 py-5 space-y-3">
          {/* ── EMAIL MODE ── */}
          {emailMode && !forgotPw && (
            <>
              {!isCarrier && (
                <button type="button" onClick={() => { setEmailMode(false); setError(""); }}
                  className="flex items-center gap-1.5 text-[10px] font-head uppercase tracking-[0.15em] transition-colors hover:opacity-80"
                  style={{ color: "rgba(255,255,255,0.35)" }}>
                  <ChevronLeft className="w-3 h-3" /> Use hub credentials
                </button>
              )}
              {/* ── CARRIER REGISTRATION FORM ── */}
              {isCarrier && showRegister ? (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-head uppercase tracking-[0.2em] text-white/50">Create Carrier Account</p>
                    <button type="button" onClick={() => { setShowRegister(false); setError(""); }}
                      className="text-[10px] font-head uppercase tracking-[0.15em] hover:opacity-80"
                      style={{ color: "rgba(255,77,0,0.7)" }}>
                      Back to sign in
                    </button>
                  </div>
                  <div>
                    {label("Company Name *")}
                    <input type="text" data-testid="register-company-name"
                      value={regForm.company_name} onChange={setReg("company_name")}
                      placeholder="ABC Trucking LLC" className={inputCls} autoFocus />
                  </div>
                  <div>
                    {label("Email Address *")}
                    <input type="email" data-testid="register-email"
                      value={regForm.email} onChange={setReg("email")}
                      placeholder="contact@yourcompany.com" className={inputCls} autoComplete="email" />
                  </div>
                  <div>
                    {label("Password *")}
                    <input type="password" data-testid="register-password"
                      value={regForm.password} onChange={setReg("password")}
                      placeholder="Min 8 characters" className={inputCls} autoComplete="new-password" />
                  </div>
                  <div>
                    {label("Confirm Password *")}
                    <input type="password" data-testid="register-confirm-password"
                      value={regForm.confirm} onChange={setReg("confirm")}
                      placeholder="Repeat password" className={inputCls} autoComplete="new-password" />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    {label("Email Address *")}
                    <input type="email" data-testid="email-login-email"
                      value={emailForm.email} onChange={(e) => setEmailForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="your@email.com" className={inputCls} autoComplete="email" autoFocus />
                  </div>
                  <div>
                    {label("Password *")}
                    <input type="password" data-testid="email-login-password"
                      value={emailForm.password} onChange={(e) => setEmailForm((f) => ({ ...f, password: e.target.value }))}
                      placeholder="Your password" className={inputCls} autoComplete="current-password" />
                  </div>
                  <button type="button" onClick={() => { setForgotPw(true); setError(""); }}
                    className="text-[10px] font-head uppercase tracking-[0.15em] transition-colors hover:opacity-80"
                    style={{ color: "rgba(255,255,255,0.3)" }}>
                    Forgot password? →
                  </button>
                </>
              )}
            </>
          )}

          {/* ── FORGOT PASSWORD MODE ── */}
          {emailMode && forgotPw && (
            <>
              <button type="button" onClick={() => { setForgotPw(false); setForgotSent(false); setError(""); }}
                className="flex items-center gap-1.5 text-[10px] font-head uppercase tracking-[0.15em] transition-colors hover:opacity-80"
                style={{ color: "rgba(255,255,255,0.35)" }}>
                <ChevronLeft className="w-3 h-3" /> Back to sign in
              </button>
              {forgotSent ? (
                <div className="flex items-center gap-3 px-3 py-3"
                  style={{ background: "rgba(74,222,128,0.05)", border: "1px solid rgba(74,222,128,0.2)" }}>
                  <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "#4ADE80" }} />
                  <p className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
                    If that email has an account, you'll receive a reset link shortly.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
                    Enter your email and we'll send a password reset link.
                  </p>
                  <div>
                    {label("Email Address *")}
                    <input type="email" data-testid="forgot-pw-email"
                      value={emailForm.email} onChange={(e) => setEmailForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="your@email.com" className={inputCls} autoFocus />
                  </div>
                </>
              )}
            </>
          )}

          {/* ── HUB CREDENTIALS MODE ── */}
          {!emailMode && (
            <>
          {(isAdmin || isCarrier) && (
            <>
              {isCarrier && (
                <>
                  <div>
                    {label("Company Name *")}
                    <input type="text" data-testid="login-carrier-name" value={form.carrier_name} onChange={set("carrier_name")} placeholder="e.g. ABC Trucking LLC" className={inputCls} />
                    <p className="text-[10px] text-white/30 mt-1">Use your full legal company name — must match exactly with your Safety team's login for cashback to link.</p>
                  </div>
                  <div>{label("Company Email *")}
                    <input type="email" data-testid="login-carrier-email" value={form.email} onChange={set("email")} placeholder="contact@yourcarrier.com" className={inputCls} />
                  </div>
                  <div>{label("Contact Phone")}
                    <input type="tel" data-testid="login-carrier-phone" value={form.carrier_phone} onChange={set("carrier_phone")} placeholder="(000) 000-0000" className={inputCls} />
                  </div>
                </>
              )}
              <div>{label("Username *")}
                <input type="text" data-testid="login-username" value={form.username} onChange={set("username")} placeholder={isAdmin ? "your@email.com" : "Hub username"} className={inputCls} autoComplete="username" />
              </div>
              <PwField label="Password *" value={form.password} onChange={set("password")} placeholder="Hub password" />
            </>
          )}

          {/* Driver */}
          {isDriver && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>{label("First Name *")}
                  <input type="text" data-testid="login-first-name" value={form.first_name} onChange={set("first_name")} placeholder="First name" className={inputCls} />
                </div>
                <div>{label("Last Name")}
                  <input type="text" data-testid="login-last-name" value={form.last_name} onChange={set("last_name")} placeholder="Last name" className={inputCls} />
                </div>
              </div>
              <div>{label("Phone Number *")}
                <input type="tel" data-testid="login-phone" value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" className={inputCls} />
              </div>
            </>
          )}

          {/* Recruiter */}
          {isRecruiter && (
            <>
              <div>{label("Your Name *")}
                <input type="text" data-testid="login-recruiter-name" value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
              </div>
              <div>{label("Email Address *")}
                <input type="email" data-testid="login-recruiter-email" value={form.email} onChange={set("email")} placeholder="your@email.com" className={inputCls} />
              </div>
              <div className="pt-1" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-2 mt-1">Hub Credentials</p>
              </div>
              <div>{label("Hub Username *")}
                <input type="text" data-testid="login-recruiter-hub-username" value={form.username} onChange={set("username")} placeholder="Recruiter" className={inputCls} autoComplete="username" />
              </div>
              <PwField label="Hub Password *" value={form.password} onChange={set("password")} placeholder="Hub password" data-testid="login-recruiter-hub-password" />
            </>
          )}

          {/* Safety */}
          {isSafety && (
            <>
              <div>
                {label("Company Name *")}
                <input type="text" data-testid="login-company-name" value={form.company_name} onChange={set("company_name")} placeholder="e.g. ABC Trucking LLC" className={inputCls} />
                <p className="text-[10px] text-white/30 mt-1">Use your full legal company name — must match the Carrier Hub login exactly to link cashback earnings.</p>
              </div>
              <div>{label("Email Address *")}
                <input type="email" data-testid="login-safety-email" value={form.email} onChange={set("email")} placeholder="your@email.com" className={inputCls} />
              </div>
              <div>{label("Direct Phone")}
                <input type="tel" data-testid="login-direct-phone" value={form.direct_phone} onChange={set("direct_phone")} placeholder="(000) 000-0000" className={inputCls} />
              </div>
              <div className="pt-1" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-2 mt-1">Hub Credentials</p>
              </div>
              <div>{label("Hub Username *")}
                <input type="text" data-testid="login-safety-hub-username" value={form.username} onChange={set("username")} placeholder="Safety" className={inputCls} autoComplete="username" />
              </div>
              <PwField label="Hub Password *" value={form.password} onChange={set("password")} placeholder="Hub password" data-testid="login-safety-hub-password" />
            </>
          )}
          </>)}  {/* end !emailMode hub credentials */}

          {error && <p className="text-red-400 text-sm bg-red-400/10 px-3 py-2">{error}</p>}
          {cooldownDisplay && (
            <div className="flex items-center gap-2 px-3 py-2" style={{ background: "rgba(245,200,66,0.08)", border: "1px solid rgba(245,200,66,0.2)" }}>
              <span className="text-[10px] font-head uppercase tracking-[0.15em] text-white/50">Locked</span>
              <span className="font-mono text-sm font-bold ml-auto" style={{ color: GOLD }}>{cooldownDisplay}</span>
            </div>
          )}
          {!emailMode && (
            <label className="flex items-center gap-2.5 cursor-pointer select-none pt-1">
              <div
                onClick={() => setRememberMe((v) => !v)}
                data-testid="remember-me-toggle"
                className="w-4 h-4 border flex items-center justify-center shrink-0 transition-colors"
                style={{ background: rememberMe ? (meta.accent || ACCENT) : "transparent", borderColor: rememberMe ? (meta.accent || ACCENT) : "rgba(255,255,255,0.2)" }}
              >
                {rememberMe && <span className="text-white text-[10px] font-bold">✓</span>}
              </div>
              <span className="text-[10px] text-white/40 font-head uppercase tracking-[0.15em]">Remember me</span>
            </label>
          )}
          {emailMode && (
            <label className="flex items-center gap-2.5 cursor-pointer select-none pt-1">
              <div
                onClick={() => setRememberMe((v) => !v)}
                data-testid="remember-me-toggle-email"
                className="w-4 h-4 border flex items-center justify-center shrink-0 transition-colors"
                style={{ background: rememberMe ? (meta.accent || ACCENT) : "transparent", borderColor: rememberMe ? (meta.accent || ACCENT) : "rgba(255,255,255,0.2)" }}
              >
                {rememberMe && <span className="text-white text-[10px] font-bold">✓</span>}
              </div>
              <span className="text-[10px] text-white/40 font-head uppercase tracking-[0.15em]">Remember me</span>
            </label>
          )}
          <button
            type="submit" data-testid="login-submit" disabled={loading || !!cooldownEnd || forgotSent}
            className="w-full flex items-center justify-center gap-2 font-head text-sm uppercase tracking-[0.2em] py-3.5 disabled:opacity-60 transition-all hover:brightness-110"
            style={{ background: emailMode ? "#222" : (meta.accent || ACCENT), color: emailMode ? "white" : (meta.accent === GOLD ? DARK : "white"),
              border: emailMode ? "1px solid rgba(255,255,255,0.15)" : "none" }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : emailMode ? <Mail className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
            {emailMode && forgotPw ? (forgotSent ? "Email Sent" : "Send Reset Link")
              : emailMode && showRegister ? "Create Account"
              : emailMode ? "Sign In" : `Enter ${meta.label}`}
          </button>

          {/* ── Carrier: toggle sign-in ↔ create account ── */}
          {isCarrier && emailMode && !forgotPw && (
            <p className="text-center text-[10px] font-head uppercase tracking-[0.15em]" style={{ color: "rgba(255,255,255,0.3)" }}>
              {showRegister ? (
                <>Already have an account?{" "}
                  <button type="button" onClick={() => { setShowRegister(false); setError(""); }}
                    className="hover:opacity-80" style={{ color: "rgba(255,77,0,0.8)" }}>
                    Sign in →
                  </button>
                </>
              ) : (
                <>New here?{" "}
                  <button type="button" data-testid="carrier-create-account-link"
                    onClick={() => { setShowRegister(true); setError(""); }}
                    className="hover:opacity-80" style={{ color: "rgba(255,77,0,0.8)" }}>
                    Create a free account →
                  </button>
                </>
              )}
            </p>
          )}

          {/* ── OR Email divider (non-driver, non-admin, non-recruiter hubs only) ── */}
          {!emailMode && !isDriver && !isAdmin && !isRecruiter && (
            <div className="pt-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
                <span className="text-[10px] font-head uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.2)" }}>OR</span>
                <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
              </div>
              <button type="button" data-testid="switch-to-email-login"
                onClick={() => { setEmailMode(true); setError(""); }}
                className="w-full flex items-center justify-center gap-2 py-2.5 font-head text-xs uppercase tracking-[0.15em] transition-all hover:brightness-110"
                style={{ background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.45)", border: "1px solid rgba(255,255,255,0.12)" }}>
                <Mail className="w-3.5 h-3.5" />
                Sign in with email & password
              </button>
            </div>
          )}

          {/* ── OR Google Sign-In (email mode, non-driver, non-admin, non-recruiter) ── */}
          {emailMode && !forgotPw && !isDriver && !isAdmin && !isRecruiter && (
            <div className="pt-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
                <span className="text-[10px] font-head uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.2)" }}>OR</span>
                <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
              </div>
              <button type="button" data-testid="google-signin-btn"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                className="w-full flex items-center justify-center gap-2.5 py-2.5 font-head text-xs uppercase tracking-[0.15em] transition-all hover:brightness-110 disabled:opacity-60"
                style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.15)" }}>
                {googleLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                )}
                {googleLoading ? "Signing in…" : "Sign in or sign up with Google"}
              </button>
            </div>
          )}
          {isDriver && (
            <button
              type="button"
              data-testid="driver-self-apply-link"
              onClick={() => setShowSelfApply(true)}
              className="w-full text-center text-[10px] font-head uppercase tracking-[0.2em] py-2 transition-colors hover:opacity-80"
              style={{ color: "rgba(255,255,255,0.3)" }}
            >
              No profile yet? Apply to join the Market →
            </button>
          )}}
        </form>
      </div>
      {showSelfApply && (
        <DriverSelfApplyModal
          onClose={() => setShowSelfApply(false)}
          referralCode={new URLSearchParams(window.location.search).get("ref") || ""}
        />
      )}
    </div>
  );
}

// ── TermsGate ────────────────────────────────────────────────────────────────
export function TermsGate({ token, onAccepted }) {
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  async function accept() {
    setLoading(true);
    try {
      await api.marketAcceptTerms(token);
      localStorage.setItem(MARKET_TERMS_KEY, "true");
      onAccepted();
    } catch {
      toast.error("Failed. Try again.");
    } finally { setLoading(false); }
  }

  const clauses = [
    ["1. Proprietary IP", "The structure, workflow, pricing models, driver cards, bidding systems and concept of the Hylas Companion Market are the confidential intellectual property of Andrej Sasaroga DBA Hylas Companion. Copying, scraping or duplicating this system is strictly prohibited."],
    ["2. Admin Discretion", "Bids go live immediately. Hylas has absolute discretion to choose and approve any bid regardless of amount or order. A bid is not binding until Hylas approves it."],
    ["3. Non-Circumvention", "All Market drivers are proprietary Hylas candidates. Hiring any driver outside the platform to avoid fees is a breach. Carriers agree to pay the selected fee when a Qualified Hire occurs."],
    ["4. Weekly Flex = Market Access", "Weekly Flex covers driver placement AND Market access. Access ends if payments stop. Free promo access applies during the launch period."],
    ["5. Driver Data Consent", "Drivers authorize display of name, state and cropped CDL face image only. CDL number, address, email and phone stay confidential."],
    ["6. Driver Royalties", "10% driver royalties from Weekly Flex are promotional rewards funded by Hylas. Drivers pay zero fees to Hylas."],
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.96)" }}>
      <div className="w-full max-w-lg flex flex-col" style={{ background: CARD, border: `1px solid rgba(245,200,66,0.3)`, maxHeight: "88vh" }}>
        <div className="px-6 pt-6 pb-4 shrink-0" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <p className="font-head text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: GOLD }}>Before You Enter</p>
          <h2 className="font-head text-base font-bold uppercase text-white">Hylas Marketplace Terms</h2>
          <p className="text-white/30 text-xs mt-1">Read the terms below, then accept.</p>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4" style={{ minHeight: 0 }}>
          <p className="text-white/60 text-sm border-l-2 pl-3 py-1" style={{ borderLeftColor: GOLD }}>
            By entering the Hylas Companion Market, you agree to these terms:
          </p>
          {clauses.map(([title, body]) => (
            <div key={title}>
              <p className="font-head text-[10px] font-bold uppercase tracking-[0.15em] text-white mb-1">{title}</p>
              <p className="text-white/50 text-xs leading-relaxed">{body}</p>
            </div>
          ))}
          <p className="text-white/20 text-[10px] pt-2">— End of Terms —</p>
        </div>
        <div className="px-6 py-4 shrink-0 space-y-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <label className="flex items-start gap-3 cursor-pointer">
            <div
              onClick={() => setChecked((c) => !c)}
              className="mt-0.5 w-5 h-5 shrink-0 flex items-center justify-center border transition-colors"
              style={checked ? { background: GOLD, border: "none" } : { borderColor: "rgba(255,255,255,0.3)" }}
            >
              {checked && (
                <svg width="11" height="9" viewBox="0 0 11 9">
                  <path d="M1 4.5l3 3 6-6" stroke="#0A0A0A" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            <span className="text-xs text-white/60 leading-relaxed">
              I have read and agree to the Hylas Companion Marketplace Terms.
            </span>
          </label>
          <button
            onClick={accept}
            disabled={!checked || loading}
            className="w-full flex items-center justify-center gap-2 font-head text-sm uppercase tracking-[0.2em] py-3.5 disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:brightness-110"
            style={{ background: GOLD, color: DARK }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Accept &amp; Enter
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Standalone Reset Password Modal (opened via ?reset_token= URL) ────────────
export function ResetPasswordModal({ resetToken, onClose, onSuccess }) {
  const [pw,  setPw]  = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [done,    setDone]    = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (pw.length < 8)   { setError("Password must be at least 8 characters."); return; }
    if (pw !== pw2)      { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.marketAccountResetPassword(resetToken, pw);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Reset failed. The link may have expired.");
    } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.92)" }}>
      <div className="w-full max-w-sm" style={{ background: CARD, border: "1px solid rgba(255,77,0,0.3)" }}>
        <div className="px-6 pt-6 pb-4 flex items-start justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="w-4 h-4" style={{ color: ACCENT }} />
              <span className="font-head text-[10px] uppercase tracking-[0.25em]" style={{ color: ACCENT }}>Hylas Market</span>
            </div>
            <h2 className="font-head text-lg font-bold uppercase tracking-[0.1em] text-white">Set New Password</h2>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white ml-4 transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {done ? (
            <>
              <div className="flex items-center gap-3 px-3 py-3"
                style={{ background: "rgba(74,222,128,0.05)", border: "1px solid rgba(74,222,128,0.2)" }}>
                <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: "#4ADE80" }} />
                <p className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>Password updated successfully!</p>
              </div>
              <button onClick={onSuccess || onClose}
                className="w-full py-3 font-head text-sm uppercase tracking-[0.2em] hover:brightness-110"
                style={{ background: ACCENT, color: "white" }}>
                Sign In Now →
              </button>
            </>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">New Password *</label>
                <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw-input"
                  placeholder="At least 8 characters" autoFocus
                  className="w-full bg-[#111] border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00] transition-colors" />
              </div>
              <div>
                <label className="block text-[10px] font-head uppercase tracking-[0.2em] text-white/50 mb-1">Confirm Password *</label>
                <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw-confirm"
                  placeholder="Repeat password"
                  className="w-full bg-[#111] border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00] transition-colors" />
              </div>
              {error && <p className="text-red-400 text-sm bg-red-400/10 px-3 py-2">{error}</p>}
              <button type="submit" disabled={loading} data-testid="reset-pw-submit"
                className="w-full flex items-center justify-center gap-2 font-head text-sm uppercase tracking-[0.2em] py-3.5 disabled:opacity-60 hover:brightness-110"
                style={{ background: ACCENT, color: "white" }}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                Set New Password
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
